/**
 * @file promotion-handlers.js
 * @description Promotion-specific handlers for each promotion type
 * @module GniibPromotions.Handlers
 *
 * Each handler provides overrides for the PromotionBase methods.
 * Add new promotion types by creating a new handler object here.
 */
(function(global) {
  'use strict';

  var GP = global.GniibPromotions = global.GniibPromotions || {};
  var Utils = GP.Utils;
  var Config = GP.Config;
  var CartManager = GP.CartManager;

  /**
   * Promotion Handlers namespace
   * @namespace GniibPromotions.Handlers
   */
  GP.Handlers = {};

  // ============================================
  // Staff Promotion Handler
  // ============================================
  GP.Handlers.Staff = {
    getConfig: function() {
      return Config.STAFF;
    },

    // TEMPORARY: same as the default loader, but restricts the variants shown.
    // Preferred: promotion.includedVariantIds, an explicit allowlist of numeric variant IDs.
    // Fallback (older server that doesn't send the allowlist yet): drop variants whose title
    // contains any keyword in promotion.excludedVariantTitleKeywords (e.g. '員工促銷優惠').
    // Remove this override once the server stops sending those keywords.
    loadProducts: async function(ctx) {
      var includedProducts = ctx.cart.promotionData?.promotion?.includedProducts;
      if (!includedProducts || includedProducts.length === 0) {
        throw new Error('No included products found');
      }

      var includedVariantIds = ctx.cart.promotionData?.promotion?.includedVariantIds;
      var useVariantAllowlist = Array.isArray(includedVariantIds);
      // Normalised to strings so the server can send either numbers or strings.
      var allowedVariantIds = useVariantAllowlist ? includedVariantIds.map(String) : [];
      var excludedKeywords = ctx.cart.promotionData?.promotion?.excludedVariantTitleKeywords || [];
      var products = [];

      for (var i = 0; i < includedProducts.length; i++) {
        var productHandle = includedProducts[i];
        try {
          var response = await fetch('https://www.g-niib.com/products/' + productHandle + '.js');
          if (response.ok) {
            var product = await response.json();

            if (useVariantAllowlist) {
              product.variants = product.variants.filter(function(variant) {
                return allowedVariantIds.indexOf(String(variant.id)) !== -1;
              });
            } else if (excludedKeywords.length > 0) {
              product.variants = product.variants.filter(function(variant) {
                var title = variant.title || '';
                return !excludedKeywords.some(function(keyword) {
                  return title.indexOf(keyword) !== -1;
                });
              });
            }

            if (product.variants.length > 0) {
              products.push(product);
            }
          }
        } catch (error) {
          console.warn('Failed to load product ' + productHandle + ':', error);
        }
      }

      if (products.length === 0) {
        throw new Error('No products could be loaded');
      }

      return products;
    },

    addToSelection: function(ctx, product, variant, quantity) {
      var itemKey = Utils.createItemKey(product.id, variant.id);
      var existingItem = CartManager.getItem(ctx.cart, itemKey);

      // Check per-product monthly limit
      var cfg = this.getConfig();
      var usedFromServer = ctx.cart.promotionData?.usage?.perProduct?.[product.handle]?.used || 0;
      var selectedForProduct = CartManager.getQuantityForProduct(ctx.cart, product.id);
      var totalAfterAdd = usedFromServer + selectedForProduct + quantity;

      if (totalAfterAdd > cfg.PER_PRODUCT_LIMIT) {
        var remaining = cfg.PER_PRODUCT_LIMIT - usedFromServer - selectedForProduct;
        if (remaining <= 0) {
          alert('此產品本月已達購買上限 (' + cfg.PER_PRODUCT_LIMIT + '件)。');
          return false;
        }
        quantity = remaining;
        alert('已加入 ' + remaining + ' 件，此產品本月已達購買上限。');
      }

      var pricing = this.resolveVariantPricing(ctx, product, variant);

      if (existingItem) {
        existingItem.quantity += quantity;
      } else {
        var item = {
          productId: product.id,
          variantId: variant.id,
          product: product,
          variant: variant,
          quantity: quantity,
          discountedPrice: pricing.price,
          basePrice: variant.price,
          hasSpecialPricing: pricing.hasSpecialPricing,
          promotionType: 'staff'
        };
        CartManager.setItem(ctx.cart, itemKey, item);
      }
      return true;
    },

    hasSpecialPricing: function(ctx, product) {
      var specialPricing = ctx.cart.promotionData?.promotion?.specialPricing;
      if (!specialPricing) return false;
      return specialPricing.hasOwnProperty(product.handle);
    },

    getSpecialPrice: function(ctx, product) {
      var specialPricing = ctx.cart.promotionData?.promotion?.specialPricing;
      if (!specialPricing) return null;
      return specialPricing[product.handle] || null;
    },

    // Single source of truth for what a staff customer pays, in cents.
    // The server now resolves the role and sends one rule per discounted variant; the
    // product-handle table below it is the pre-variantPricing contract, kept live so an
    // older app version keeps working.
    resolveVariantPricing: function(ctx, product, variant) {
      var variantPricing = ctx.cart.promotionData?.promotion?.variantPricing;
      var rule = variantPricing ? variantPricing[String(variant.id)] : null;

      if (rule) {
        if (rule.model === 'fixed') {
          return {
            // amountCents is already in cents, like variant.price. Clamping keeps a stale
            // rule from presenting a price above list as a discount.
            price: Math.min(rule.amountCents, variant.price),
            suffix: '特別優惠',
            title: rule.title || null,
            hasSpecialPricing: true
          };
        }
        return {
          price: variant.price * (1 - rule.rate),
          suffix: Math.round(rule.rate * 100) + '%折扣',
          title: rule.title || null,
          hasSpecialPricing: false
        };
      }

      if (this.hasSpecialPricing(ctx, product)) {
        return {
          price: this.getSpecialPrice(ctx, product) * 100, // legacy table is in dollars
          suffix: '特別優惠',
          title: null,
          hasSpecialPricing: true
        };
      }

      // A server that sends variantPricing enumerates every discounted variant, so a variant
      // missing from it is deliberately full price. Only older servers still mean "half off
      // whatever the allowlist returned".
      if (variantPricing) {
        return { price: variant.price, suffix: null, title: null, hasSpecialPricing: false };
      }

      return {
        price: variant.price * 0.5,
        suffix: '50%折扣',
        title: null,
        hasSpecialPricing: false
      };
    },

    getDiscountNameForItem: function(ctx, item) {
      var variantPricing = ctx.cart.promotionData?.promotion?.variantPricing;
      var rule = variantPricing ? variantPricing[String(item.variantId)] : null;
      if (rule?.title) {
        return rule.title;
      }

      var roleKey = ctx.cart.promotionData?.roleKey;
      var isGNiiBOrMicroSigX = roleKey === 'gniib_staff' || roleKey === 'microsigx_staff';

      if (item.hasSpecialPricing && isGNiiBOrMicroSigX) {
        return ctx.cart.promotionData?.promotion?.specialPricingTitle || '💙 GB 員工 M3XTRA Pro 優惠';
      }
      return ctx.cart.promotionData?.promotion?.defaultPromotionTitle || '💼 員工優惠';
    },

    getDiscountName: function(ctx) {
      return ctx.cart.promotionData?.promotion?.defaultPromotionTitle || '💼 員工優惠';
    },

    renderPriceHtml: function(ctx, product, variant) {
      var cfg = this.getConfig();
      var usedFromServer = ctx.cart.promotionData?.usage?.perProduct?.[product.handle]?.used || 0;
      var selectedForProduct = CartManager.getQuantityForProduct(ctx.cart, product.id);
      var totalUsed = usedFromServer + selectedForProduct;

      var usageBadge = '<div class="product-usage-badge">已用 ' + totalUsed + '/' + cfg.PER_PRODUCT_LIMIT + '</div>';

      var pricing = this.resolveVariantPricing(ctx, product, variant);

      // Defensive: the server sends a price for every variant it puts on the allowlist,
      // so this should not be reachable. Rendering the plain price without the
      // .discounted-price class matters anyway - that class is what makes a figure read
      // as a discount, and a full price must not.
      if (!pricing.suffix) {
        return usageBadge + '<span>$' + Utils.formatPrice(variant.price) + '</span>';
      }

      return usageBadge +
             '<span class="discounted-price">$' + Utils.formatPrice(pricing.price) + ' <small>(' + pricing.suffix + ')</small></span>' +
             '<span class="original-price">$' + Utils.formatPrice(variant.price) + '</span>';
    },

    renderUsageDisplay: function(ctx, usageInfoEl) {
      var cfg = this.getConfig();
      var usageHtml = '<div class="usage-item">' +
        '<span class="usage-label">每款產品限購:</span>' +
        '<span class="usage-value">' + cfg.PER_PRODUCT_LIMIT + ' 件/月</span>' +
        '</div>' +
        '<div class="usage-item">' +
        '<span class="usage-label">折扣:</span>' +
        '<span class="usage-value">50% (半價)</span>' +
        '</div>';

      // Update desktop usage info
      usageInfoEl.style.display = 'block';
      usageInfoEl.innerHTML = usageHtml;

      // Update mobile usage info
      var mobileUsageInfo = document.getElementById('mobile-usage-info');
      if (mobileUsageInfo) {
        mobileUsageInfo.style.display = 'block';
        mobileUsageInfo.innerHTML = usageHtml;
      }
    }
  };

  // ============================================
  // SKHWC Staff Promotion Handler
  // ============================================
  GP.Handlers.Skhwc = {
    getConfig: function() {
      return Config.SKHWC;
    },

    validatePeriod: function(ctx) {
      var isActive = ctx.cart.promotionData?.promotion?.active;
      if (!isActive) {
        throw new Error('此優惠已結束。如有疑問，請聯絡客戶服務。');
      }
    },

    addToSelection: function(ctx, product, variant, quantity) {
      var cfg = this.getConfig();
      var currentTotal = CartManager.getTotalQuantity(ctx.cart);
      var newTotal = currentTotal + quantity;

      if (newTotal > cfg.MAX_QUANTITY) {
        var remaining = cfg.MAX_QUANTITY - currentTotal;
        if (remaining > 0) {
          alert('每個訂單最多只能購買 ' + cfg.MAX_QUANTITY + ' 件產品。您最多還可以添加 ' + remaining + ' 件。');
        } else {
          alert('已達到最大購買數量 (' + cfg.MAX_QUANTITY + ' 件)。');
        }
        return false;
      }

      var itemKey = Utils.createItemKey(product.id, variant.id);
      var existingItem = CartManager.getItem(ctx.cart, itemKey);

      if (existingItem) {
        existingItem.quantity += quantity;
      } else {
        var item = {
          productId: product.id,
          variantId: variant.id,
          product: product,
          variant: variant,
          quantity: quantity,
          discountedPrice: variant.price,
          basePrice: variant.price,
          hasDiscount: false,
          promotionType: 'skhwc_staff'
        };
        CartManager.setItem(ctx.cart, itemKey, item);
      }
      return true;
    },

    recalculatePricing: function(ctx) {
      var cfg = this.getConfig();
      var totalQuantity = CartManager.getTotalQuantity(ctx.cart);
      var hasDiscount = totalQuantity >= cfg.MIN_DISCOUNT_QUANTITY && this.isDiscountCampaignAvailable(ctx);

      CartManager.forEach(ctx.cart, function(item) {
        if (hasDiscount) {
          item.discountedPrice = item.basePrice * (1 - cfg.DISCOUNT_RATE);
          item.hasDiscount = true;
        } else {
          item.discountedPrice = item.basePrice;
          item.hasDiscount = false;
        }
      });
    },

    isDiscountCampaignAvailable: function(ctx) {
      var campaigns = ctx.cart.promotionData?.promotion?.campaigns;
      if (!campaigns) return false;
      var discountCampaign = campaigns.find(function(c) {
        return c.type === 'percentage_discount';
      });
      return discountCampaign && !discountCampaign.used;
    },

    isGiftCampaignAvailable: function(ctx) {
      var campaigns = ctx.cart.promotionData?.promotion?.campaigns;
      if (!campaigns) return false;
      var giftCampaign = campaigns.find(function(c) {
        return c.type === 'free_gift';
      });
      return giftCampaign && !giftCampaign.used;
    },

    renderCampaigns: function(ctx) {
      var campaignsEl = ctx.elements.CAMPAIGNS;
      var campaigns = ctx.cart.promotionData?.promotion?.campaigns;
      if (!campaignsEl || !campaigns) return;

      var html = '<div class="campaigns-header"><h2>優惠詳情</h2><p class="campaigns-note">優惠只可以享用一次</p></div>';
      html += '<div class="campaigns-grid">';

      campaigns.forEach(function(campaign, index) {
        var isUsed = campaign.used;
        var statusClass = isUsed ? 'used' : 'available';
        var statusText = isUsed ? '已使用' : '可使用';

        html += '<div class="campaign-card ' + statusClass + '">' +
          '<div class="campaign-header">' +
          '<h3 class="campaign-number">優惠' + (index + 1) + '</h3>' +
          '<span class="campaign-status badge-' + statusClass + '">' + statusText + '</span>' +
          '</div>' +
          '<p class="campaign-description">' + campaign.description + '</p>' +
          (isUsed && campaign.usedAt ? '<p class="campaign-used-date">使用日期: ' + Utils.formatDate(campaign.usedAt) + '</p>' : '') +
          '</div>';
      });

      html += '</div>';
      campaignsEl.innerHTML = html;
    },

    renderPriceHtml: function(ctx, product, variant) {
      var campaignNotUsed = this.isDiscountCampaignAvailable(ctx);
      if (campaignNotUsed) {
        var discountedPrice = variant.price * (1 - Config.SKHWC.DISCOUNT_RATE);
        return '<span class="discounted-price">$' + Utils.formatPrice(discountedPrice) + ' <small>(買2件或以上享8折)</small></span>' +
               '<span class="original-price">$' + Utils.formatPrice(variant.price) + '</span>';
      }
      return '<span class="product-price-full">$' + Utils.formatPrice(variant.price) + '</span>';
    },

    renderUsageDisplay: function(ctx, usageInfoEl) {
      var cfg = this.getConfig();
      var totalQuantity = CartManager.getTotalQuantity(ctx.cart);
      var needsMore = cfg.MIN_DISCOUNT_QUANTITY - totalQuantity;

      var html = '<div class="usage-item">' +
        '<span class="usage-label">已選數量:</span>' +
        '<span class="usage-value">' + totalQuantity + ' / ' + cfg.MAX_QUANTITY + '</span>' +
        '</div>';

      if (needsMore > 0) {
        html += '<div class="usage-item">' +
          '<span class="usage-label" style="color: #ff6b00;">還需:</span>' +
          '<span class="usage-value" style="color: #ff6b00;">' + needsMore + ' 件 (享8折優惠)</span>' +
          '</div>';
      } else if (totalQuantity >= cfg.MIN_DISCOUNT_QUANTITY) {
        html += '<div class="usage-item">' +
          '<span class="usage-label" style="color: #28a745;">✓ 已符合:</span>' +
          '<span class="usage-value" style="color: #28a745;">8折優惠</span>' +
          '</div>';
      }

      // Update desktop usage info
      usageInfoEl.style.display = 'block';
      usageInfoEl.innerHTML = html;

      // Update mobile usage info
      var mobileUsageInfo = document.getElementById('mobile-usage-info');
      if (mobileUsageInfo) {
        mobileUsageInfo.style.display = 'block';
        mobileUsageInfo.innerHTML = html;
      }
    },

    calculateItemTotal: function(ctx, item) {
      return item.discountedPrice * item.quantity;
    }
  };

  // ============================================
  // FUBON Promotion Handler
  // ============================================
  GP.Handlers.Fubon = {
    getConfig: function() {
      return Config.FUBON;
    },

    validatePeriod: function(ctx) {
      var periodInfo = ctx.cart.promotionData?.periodInfo;
      if (!periodInfo?.isActive) {
        throw new Error('此優惠期限為2025年11月19日至20日。如有疑問，請聯絡客戶服務。');
      }
    },

    loadProducts: async function(ctx) {
      var includedVariants = ctx.cart.promotionData?.promotion?.includedVariants;
      if (!includedVariants || includedVariants.length === 0) {
        throw new Error('No included variants found in promotion data');
      }

      var products = [];
      var seen = new Set();

      for (var i = 0; i < includedVariants.length; i++) {
        var variantData = includedVariants[i];
        var key = variantData.productHandle + '-' + variantData.variantId;
        if (seen.has(key)) continue;
        seen.add(key);

        try {
          var response = await fetch('https://www.g-niib.com/products/' + variantData.productHandle + '.js');
          if (response.ok) {
            var product = await response.json();
            var numericVariantId = Utils.extractVariantId(variantData.variantId);
            var specificVariant = product.variants.find(function(v) { return v.id === numericVariantId; });

            if (specificVariant) {
              products.push({
                ...product,
                variants: [specificVariant],
                _fubonDiscountPercentage: variantData.discountPercentage,
                _fubonDiscountRate: variantData.discountRate,
                _promotionVariantId: numericVariantId
              });
            }
          }
        } catch (error) {
          console.warn('Failed to load product:', error);
        }
      }

      if (products.length === 0) {
        throw new Error('No products could be loaded');
      }

      return products;
    },

    addToSelection: function(ctx, product, variant, quantity) {
      var cfg = this.getConfig();
      var currentTotal = CartManager.getTotalQuantity(ctx.cart);
      var newTotal = currentTotal + quantity;

      if (newTotal > cfg.MAX_DISCOUNTED) {
        alert('富邦優惠最多只能購買 ' + cfg.MAX_DISCOUNTED + ' 件產品。目前已有 ' + currentTotal + ' 件。');
        return false;
      }

      var itemKey = Utils.createItemKey(product.id, variant.id);
      var existingItem = CartManager.getItem(ctx.cart, itemKey);
      var productDiscount = this.getProductDiscount(product);

      if (existingItem) {
        existingItem.quantity += quantity;
      } else {
        var item = {
          productId: product.id,
          variantId: variant.id,
          product: product,
          variant: variant,
          quantity: quantity,
          discountPercentage: productDiscount,
          discountedPrice: variant.price * (1 - productDiscount / 100),
          basePrice: variant.price,
          quantityWithDiscount: 0,
          quantityFullPrice: 0,
          promotionType: 'fubon'
        };
        CartManager.setItem(ctx.cart, itemKey, item);
      }
      return true;
    },

    recalculatePricing: function(ctx) {
      // ALL-OR-NOTHING logic (matches original):
      // - If total quantity is EVEN AND >= MIN_QUANTITY: ALL items get discount
      // - If total quantity is ODD or < MIN_QUANTITY: NO items get discount
      var cfg = this.getConfig();
      var totalQuantity = CartManager.getTotalQuantity(ctx.cart);
      var isUsed = ctx.cart.promotionData?.usage?.used;
      var isEven = totalQuantity % 2 === 0;
      var allGetDiscount = isEven && totalQuantity >= cfg.MIN_QUANTITY && !isUsed;

      CartManager.forEach(ctx.cart, function(item) {
        if (allGetDiscount) {
          // All items get discount
          item.quantityWithDiscount = item.quantity;
          item.quantityFullPrice = 0;
        } else {
          // No items get discount
          item.quantityWithDiscount = 0;
          item.quantityFullPrice = item.quantity;
        }
      });
    },

    getProductDiscount: function(product) {
      return product._fubonDiscountPercentage || 20;
    },

    calculateItemTotal: function(ctx, item) {
      // Uses quantityWithDiscount/quantityFullPrice set by recalculatePricing
      var discountedTotal = item.discountedPrice * item.quantityWithDiscount;
      var fullPriceTotal = item.basePrice * item.quantityFullPrice;
      return discountedTotal + fullPriceTotal;
    },

    renderPriceHtml: function(ctx, product, variant) {
      var isUsed = ctx.cart.promotionData?.usage?.used;
      var productDiscount = this.getProductDiscount(product);

      if (isUsed) {
        return '<span class="product-price-full">$' + Utils.formatPrice(variant.price) + '</span>';
      }

      var discountedPrice = variant.price * (1 - productDiscount / 100);
      var discountLabel = productDiscount === 30 ? '7折' : '8折';
      return '<span class="discounted-price">$' + Utils.formatPrice(discountedPrice) + ' <small>(購買 雙數數量 即享 ' + discountLabel + ' 優惠)</small></span>' +
             '<span class="original-price">$' + Utils.formatPrice(variant.price) + '</span>';
    },

    renderUsageDisplay: function(ctx, usageInfoEl) {
      var cfg = this.getConfig();
      var totalQuantity = CartManager.getTotalQuantity(ctx.cart);
      var isUsed = ctx.cart.promotionData?.usage?.used;
      var needsMore = cfg.MIN_QUANTITY - totalQuantity;
      var discountedCount = Math.min(totalQuantity, cfg.MAX_DISCOUNTED);
      var isOddQuantity = totalQuantity % 2 === 1;
      var mobileUsageInfo = document.getElementById('mobile-usage-info');

      usageInfoEl.style.display = 'block';

      if (isUsed) {
        var usedHtml = '<div class="usage-item usage-item-fubon">' +
          '<span class="usage-label" style="color: #d72c0d;">已使用:</span>' +
          '<span class="usage-value" style="color: #d72c0d;">此優惠已使用</span>' +
          '</div>';
        usageInfoEl.innerHTML = usedHtml;
        if (mobileUsageInfo) {
          mobileUsageInfo.style.display = 'block';
          mobileUsageInfo.innerHTML = usedHtml;
        }
        return;
      }

      var html = '<div class="usage-item usage-item-fubon">' +
        '<span class="usage-label">優惠數量:</span>' +
        '<span class="usage-value">' + discountedCount + ' / ' + cfg.MAX_DISCOUNTED + '</span>' +
        '</div>';

      if (needsMore > 0) {
        html += '<div class="usage-item usage-item-fubon">' +
          '<span class="usage-label" style="color: #ff6b00;">還需:</span>' +
          '<span class="usage-value" style="color: #ff6b00;">' + needsMore + ' 件 (享優惠)</span>' +
          '</div>';
      }

      if (isOddQuantity && totalQuantity < cfg.MAX_DISCOUNTED && totalQuantity >= cfg.MIN_QUANTITY) {
        html += '<div class="usage-item usage-item-fubon">' +
          '<span class="usage-label" style="color: #00a86b;">提示:</span>' +
          '<span class="usage-value" style="color: #00a86b;">再加1件即享優惠價！</span>' +
          '</div>';
      }

      // Update desktop usage info
      usageInfoEl.innerHTML = html;

      // Update mobile usage info
      if (mobileUsageInfo) {
        mobileUsageInfo.style.display = 'block';
        mobileUsageInfo.innerHTML = html;
      }
    }
  };

  // ============================================
  // CITIBANK Promotion Handler (similar to FUBON)
  // ============================================
  GP.Handlers.Citibank = {
    getConfig: function() {
      return Config.CITIBANK;
    },

    validatePeriod: function(ctx) {
      var periodInfo = ctx.cart.promotionData?.periodInfo;
      if (!periodInfo?.isActive) {
        throw new Error('此優惠期限為2025年12月11日至12日。如有疑問，請聯絡客戶服務。');
      }
    },

    loadProducts: GP.Handlers.Fubon.loadProducts,

    addToSelection: function(ctx, product, variant, quantity) {
      var cfg = this.getConfig();
      var currentTotal = CartManager.getTotalQuantity(ctx.cart);
      var newTotal = currentTotal + quantity;

      if (newTotal > cfg.MAX_DISCOUNTED) {
        alert('花旗銀行優惠最多只能購買 ' + cfg.MAX_DISCOUNTED + ' 件產品。目前已有 ' + currentTotal + ' 件。');
        return false;
      }

      var itemKey = Utils.createItemKey(product.id, variant.id);
      var existingItem = CartManager.getItem(ctx.cart, itemKey);
      var productDiscount = this.getProductDiscount(product);

      if (existingItem) {
        existingItem.quantity += quantity;
      } else {
        var item = {
          productId: product.id,
          variantId: variant.id,
          product: product,
          variant: variant,
          quantity: quantity,
          discountPercentage: productDiscount,
          discountedPrice: variant.price * (1 - productDiscount / 100),
          basePrice: variant.price,
          quantityWithDiscount: 0,
          quantityFullPrice: 0,
          promotionType: 'citibank'
        };
        CartManager.setItem(ctx.cart, itemKey, item);
      }
      return true;
    },

    recalculatePricing: GP.Handlers.Fubon.recalculatePricing,

    getProductDiscount: function(product) {
      return product._citibankDiscountPercentage || 20;
    },

    calculateItemTotal: GP.Handlers.Fubon.calculateItemTotal,

    renderPriceHtml: function(ctx, product, variant) {
      var isUsed = ctx.cart.promotionData?.usage?.used;
      var productDiscount = this.getProductDiscount(product);

      if (isUsed) {
        return '<span class="product-price-full">$' + Utils.formatPrice(variant.price) + '</span>';
      }

      var discountedPrice = variant.price * (1 - productDiscount / 100);
      var discountLabel = productDiscount === 30 ? '7折' : '8折';
      return '<span class="discounted-price">$' + Utils.formatPrice(discountedPrice) + ' <small>(購買 雙數數量 即享 ' + discountLabel + ' 優惠)</small></span>' +
             '<span class="original-price">$' + Utils.formatPrice(variant.price) + '</span>';
    },

    renderUsageDisplay: GP.Handlers.Fubon.renderUsageDisplay
  };

  // ============================================
  // KWH Macau Promotion Handler
  // ============================================
  GP.Handlers.KwhMacau = {
    getConfig: function() {
      return Config.KWH_MACAU;
    },

    loadProducts: async function(ctx) {
      var includedProducts = ctx.cart.promotionData?.promotion?.includedProducts;
      var variantsByProduct = ctx.cart.promotionData?.promotion?.variantsByProduct;

      if (!includedProducts || includedProducts.length === 0) {
        throw new Error('No included products found in promotion data');
      }

      var products = [];

      for (var i = 0; i < includedProducts.length; i++) {
        var productHandle = includedProducts[i];
        try {
          var response = await fetch('https://www.g-niib.com/products/' + productHandle + '.js');
          if (response.ok) {
            var product = await response.json();
            var allowedVariants = variantsByProduct?.[productHandle] || [];
            var allowedVariantIds = allowedVariants.map(function(v) {
              return Utils.extractVariantId(v.variantId || v.id);
            });

            var filteredVariants = product.variants.filter(function(v) {
              return allowedVariantIds.indexOf(v.id) !== -1;
            });

            if (filteredVariants.length > 0) {
              product.variants = filteredVariants;
              products.push(product);
            }
          }
        } catch (error) {
          console.warn('Failed to load product:', error);
        }
      }

      if (products.length === 0) {
        throw new Error('No products could be loaded');
      }

      return products;
    },

    addToSelection: function(ctx, product, variant, quantity) {
      var itemKey = Utils.createItemKey(product.id, variant.id);
      var existingItem = CartManager.getItem(ctx.cart, itemKey);
      var cfg = this.getConfig();
      var discountedPrice = variant.price * cfg.DISCOUNT_RATE;

      if (existingItem) {
        existingItem.quantity += quantity;
      } else {
        var item = {
          productId: product.id,
          variantId: variant.id,
          product: product,
          variant: variant,
          quantity: quantity,
          discountedPrice: discountedPrice,
          basePrice: variant.price,
          promotionType: 'kwh_macau'
        };
        CartManager.setItem(ctx.cart, itemKey, item);
      }
      return true;
    },

    renderPriceHtml: function(ctx, product, variant) {
      var cfg = this.getConfig();
      var discountedPrice = variant.price * cfg.DISCOUNT_RATE;
      return '<span class="discounted-price">$' + Utils.formatPrice(discountedPrice) + ' <small>(50%折扣)</small></span>' +
             '<span class="original-price">$' + Utils.formatPrice(variant.price) + '</span>';
    },

    renderUsageDisplay: function(ctx, usageInfoEl) {
      // Hide desktop usage info
      usageInfoEl.style.display = 'none';
      // Also hide mobile usage info (no limits for this promotion)
      var mobileUsageInfo = document.getElementById('mobile-usage-info');
      if (mobileUsageInfo) {
        mobileUsageInfo.style.display = 'none';
      }
    }
  };

  // ============================================
  // Space VIP Promotion Handler (admin-managed variant selection)
  // ============================================
  GP.Handlers.SpaceVip = {
    getConfig: function() {
      return Config.SPACE_VIP;
    },

    // New Space VIP responses carry an explicit variant allowlist. If this theme
    // is deployed before the app, the old response has no allowlist and all
    // variants remain visible, preserving the current storefront behaviour.
    loadProducts: async function(ctx) {
      var includedProducts = ctx.cart.promotionData?.promotion?.includedProducts;
      if (!includedProducts || includedProducts.length === 0) {
        throw new Error('No included products found in promotion data');
      }

      var includedVariantIds = ctx.cart.promotionData?.promotion?.includedVariantIds;
      var useVariantAllowlist = Array.isArray(includedVariantIds);
      var allowedVariantIds = useVariantAllowlist ? includedVariantIds.map(String) : [];
      var products = [];

      for (var i = 0; i < includedProducts.length; i++) {
        var productHandle = includedProducts[i];

        try {
          var response = await fetch('https://www.g-niib.com/products/' + productHandle + '.js');
          if (response.ok) {
            var product = await response.json();

            if (useVariantAllowlist) {
              product.variants = product.variants.filter(function(variant) {
                return allowedVariantIds.indexOf(String(variant.id)) !== -1;
              });
            }

            if (product.variants.length > 0) {
              products.push(product);
            }
          }
        } catch (error) {
          console.warn('Failed to load Space VIP product ' + productHandle + ':', error);
        }
      }

      if (products.length === 0) {
        throw new Error('No products could be loaded');
      }

      return products;
    },

    resolveVariantPricing: function(ctx, variant) {
      var variantPricing = ctx.cart.promotionData?.promotion?.variantPricing;
      var rule = variantPricing ? variantPricing[String(variant.id)] : null;

      if (rule && rule.model === 'percentage' && typeof rule.rate === 'number') {
        return {
          price: variant.price * (1 - rule.rate),
          rate: rule.rate,
          title: rule.title || null
        };
      }

      // A new response enumerates every discounted variant. Missing pricing in
      // that contract means full price; never invent a discount for it.
      if (variantPricing) {
        return { price: variant.price, rate: 0, title: null };
      }

      // Old backend compatibility during a theme-first deployment.
      var responseDiscount = ctx.cart.promotionData?.promotion?.discount;
      var fallbackRate = typeof responseDiscount === 'number'
        ? responseDiscount / 100
        : this.getConfig().DISCOUNT_RATE;

      return {
        price: variant.price * (1 - fallbackRate),
        rate: fallbackRate,
        title: null
      };
    },

    addToSelection: function(ctx, product, variant, quantity) {
      var itemKey = Utils.createItemKey(product.id, variant.id);
      var existingItem = CartManager.getItem(ctx.cart, itemKey);
      var resolved = this.resolveVariantPricing(ctx, variant);

      if (existingItem) {
        existingItem.quantity += quantity;
      } else {
        CartManager.setItem(ctx.cart, itemKey, {
          productId: product.id,
          variantId: variant.id,
          product: product,
          variant: variant,
          quantity: quantity,
          discountedPrice: resolved.price,
          basePrice: variant.price,
          promotionType: 'space_vip'
        });
      }

      return true;
    },

    renderPriceHtml: function(ctx, product, variant) {
      var resolved = this.resolveVariantPricing(ctx, variant);

      if (resolved.rate <= 0) {
        return '<span class="product-price-full">$' + Utils.formatPrice(variant.price) + '</span>';
      }

      var discountPercent = Math.round(resolved.rate * 100);
      return '<span class="discounted-price">$' + Utils.formatPrice(resolved.price) + ' <small>(' + discountPercent + '%折扣)</small></span>' +
             '<span class="original-price">$' + Utils.formatPrice(variant.price) + '</span>';
    },

    renderUsageDisplay: GP.Handlers.KwhMacau.renderUsageDisplay
  };

  // ============================================
  // IBD Group Promotion Handler
  // ============================================
  GP.Handlers.IbdGroup = {
    getConfig: function() {
      return Config.IBD_GROUP;
    },

    loadProducts: async function(ctx) {
      var includedVariants = ctx.cart.promotionData?.promotion?.includedVariants;
      if (!includedVariants || includedVariants.length === 0) {
        throw new Error('No included variants found in promotion data');
      }

      var products = [];
      var seen = new Set();

      for (var i = 0; i < includedVariants.length; i++) {
        var variantData = includedVariants[i];
        var key = variantData.productHandle + '-' + variantData.variantId;
        if (seen.has(key)) continue;
        seen.add(key);

        try {
          var response = await fetch('https://www.g-niib.com/products/' + variantData.productHandle + '.js');
          if (response.ok) {
            var product = await response.json();
            var numericVariantId = Utils.extractVariantId(variantData.variantId);
            var specificVariant = product.variants.find(function(v) { return v.id === numericVariantId; });

            if (specificVariant) {
              products.push({
                ...product,
                variants: [specificVariant],
                _promotionVariantId: numericVariantId
              });
            }
          }
        } catch (error) {
          console.warn('Failed to load product:', error);
        }
      }

      if (products.length === 0) {
        throw new Error('No products could be loaded');
      }

      return products;
    },

    addToSelection: function(ctx, product, variant, quantity) {
      var cfg = this.getConfig();
      var perVariantLimit = ctx.cart.promotionData?.promotion?.perVariantLimit || cfg.DEFAULT_PER_VARIANT_LIMIT;
      var alreadyUsed = CartManager.getVariantUsageFromServer(ctx.cart, variant.id);

      var itemKey = Utils.createItemKey(product.id, variant.id);
      var existingItem = CartManager.getItem(ctx.cart, itemKey);

      if (existingItem) {
        existingItem.quantity += quantity;
      } else {
        var item = {
          productId: product.id,
          variantId: variant.id,
          product: product,
          variant: variant,
          quantity: quantity,
          discountedPrice: variant.price * (1 - cfg.DISCOUNT_RATE),
          fullPrice: variant.price,
          basePrice: variant.price,
          quantityWithDiscount: 0,
          quantityFullPrice: 0,
          alreadyUsedCount: alreadyUsed,
          promotionType: 'ibd_group'
        };
        CartManager.setItem(ctx.cart, itemKey, item);
      }
      return true;
    },

    recalculatePricing: function(ctx) {
      var cfg = this.getConfig();
      var perVariantLimit = ctx.cart.promotionData?.promotion?.perVariantLimit || cfg.DEFAULT_PER_VARIANT_LIMIT;

      CartManager.forEach(ctx.cart, function(item) {
        var alreadyUsed = CartManager.getVariantUsageFromServer(ctx.cart, item.variantId);
        var remainingDiscounted = Math.max(0, perVariantLimit - alreadyUsed);
        item.quantityWithDiscount = Math.min(item.quantity, remainingDiscounted);
        item.quantityFullPrice = item.quantity - item.quantityWithDiscount;
      });
    },

    calculateItemTotal: function(ctx, item) {
      var discountedTotal = item.discountedPrice * item.quantityWithDiscount;
      var fullPriceTotal = item.fullPrice * item.quantityFullPrice;
      return discountedTotal + fullPriceTotal;
    },

    renderPriceHtml: function(ctx, product, variant) {
      var cfg = this.getConfig();
      var perVariantLimit = ctx.cart.promotionData?.promotion?.perVariantLimit || cfg.DEFAULT_PER_VARIANT_LIMIT;
      var alreadyUsed = CartManager.getVariantUsageFromServer(ctx.cart, variant.id);
      var selectedInCart = CartManager.getQuantityForVariant(ctx.cart, variant.id);
      var totalUsed = alreadyUsed + selectedInCart;
      var remainingDiscounted = Math.max(0, perVariantLimit - totalUsed);

      var usageBadge = '<div class="product-usage-badge">已用 ' + totalUsed + '/' + perVariantLimit + '</div>';

      if (remainingDiscounted > 0) {
        var discountedPrice = variant.price * (1 - cfg.DISCOUNT_RATE);
        return usageBadge +
          '<span class="discounted-price">$' + Utils.formatPrice(discountedPrice) + ' <small>(75折)</small></span>' +
          '<span class="original-price">$' + Utils.formatPrice(variant.price) + '</span>';
      }
      return usageBadge + '<span class="product-price-full">$' + Utils.formatPrice(variant.price) + '</span>';
    },

    renderUsageDisplay: function(ctx, usageInfoEl) {
      // Hide desktop usage info
      usageInfoEl.style.display = 'none';
      // Also hide mobile usage info (per-variant limits shown on cards)
      var mobileUsageInfo = document.getElementById('mobile-usage-info');
      if (mobileUsageInfo) {
        mobileUsageInfo.style.display = 'none';
      }
    }
  };

  // ============================================
  // Partner Staff Promotion Handler
  // ============================================
  GP.Handlers.PartnerStaff = {
    getConfig: function() {
      return Config.PARTNER_STAFF;
    },

    loadProducts: GP.Handlers.IbdGroup.loadProducts,

    addToSelection: function(ctx, product, variant, quantity) {
      var cfg = this.getConfig();
      var perVariantLimit = ctx.cart.promotionData?.promotion?.perVariantLimit || cfg.PER_VARIANT_LIMIT;
      var alreadyUsed = CartManager.getVariantUsageFromServer(ctx.cart, variant.id);
      var alreadySelected = CartManager.getQuantityForVariant(ctx.cart, variant.id);
      var totalAfterAdd = alreadyUsed + alreadySelected + quantity;

      if (totalAfterAdd > perVariantLimit) {
        var remaining = perVariantLimit - alreadyUsed - alreadySelected;
        if (remaining <= 0) {
          alert('此款產品本月已達購買上限 (' + perVariantLimit + '件)。');
        } else {
          alert('此款產品本月最多還可購買 ' + remaining + ' 件。');
        }
        return false;
      }

      var itemKey = Utils.createItemKey(product.id, variant.id);
      var existingItem = CartManager.getItem(ctx.cart, itemKey);
      var discountedPrice = variant.price * 0.5;

      if (existingItem) {
        existingItem.quantity += quantity;
      } else {
        var item = {
          productId: product.id,
          variantId: variant.id,
          product: product,
          variant: variant,
          quantity: quantity,
          discountedPrice: discountedPrice,
          basePrice: variant.price,
          promotionType: 'partner_staff'
        };
        CartManager.setItem(ctx.cart, itemKey, item);
      }
      return true;
    },

    renderPriceHtml: function(ctx, product, variant) {
      var cfg = this.getConfig();
      var perVariantLimit = ctx.cart.promotionData?.promotion?.perVariantLimit || cfg.PER_VARIANT_LIMIT;
      var alreadyUsed = CartManager.getVariantUsageFromServer(ctx.cart, variant.id);
      var selectedInCart = CartManager.getQuantityForVariant(ctx.cart, variant.id);
      var totalUsed = alreadyUsed + selectedInCart;
      var remainingUnits = Math.max(0, perVariantLimit - totalUsed);

      var usageBadge = '<div class="product-usage-badge">已用 ' + totalUsed + '/' + perVariantLimit + '</div>';

      if (remainingUnits > 0) {
        var discountedPrice = variant.price * 0.5;
        return usageBadge +
          '<span class="discounted-price">$' + Utils.formatPrice(discountedPrice) + ' <small>(50%折扣)</small></span>' +
          '<span class="original-price">$' + Utils.formatPrice(variant.price) + '</span>';
      }
      return usageBadge + '<span class="product-price-full" style="color: #d72c0d;">已達上限</span>';
    },

    renderUsageDisplay: function(ctx, usageInfoEl) {
      // Hide desktop usage info
      usageInfoEl.style.display = 'none';
      // Also hide mobile usage info (per-variant limits shown on cards)
      var mobileUsageInfo = document.getElementById('mobile-usage-info');
      if (mobileUsageInfo) {
        mobileUsageInfo.style.display = 'none';
      }
    }
  };

  // ============================================
  // CUMC Promotion Handler
  // ============================================
  GP.Handlers.Cumc = {
    getConfig: function() {
      return Config.CUMC;
    },

    validatePeriod: function(ctx) {
      // ⚠️ BACKDOOR (testing only): whitelisted accounts skip the period/time gate.
      var bypassIds = (this.getConfig() || {}).PERIOD_BYPASS_CUSTOMER_IDS || [];
      var customerId = GP.CustomerService.getCustomerId();
      if (customerId && bypassIds.indexOf(String(customerId)) !== -1) {
        return;
      }

      var periodInfo = ctx.cart.promotionData?.periodInfo;
      if (!periodInfo?.isActive) {
        throw new Error('此優惠期限為每月20日00:00至25日23:59（香港時間）。如有疑問，請聯絡客戶服務。');
      }
    },

    loadProducts: async function(ctx) {
      var rule1 = ctx.cart.promotionData?.promotion?.rule1;
      var rule2 = ctx.cart.promotionData?.promotion?.rule2;

      if (!rule1 && !rule2) {
        throw new Error('No promotion rules found');
      }

      var products = [];
      var cfg = this.getConfig();

      // Load Rule 1 products (each variant: fixed price OR percentage discount)
      if (rule1?.variants && Object.keys(rule1.variants).length > 0) {
        var seenRule1 = new Set();

        for (var rule1Gid in rule1.variants) {
          var rule1Data = rule1.variants[rule1Gid];
          var rule1Handle = rule1Data.productHandle;
          var rule1Key = rule1Handle + '-' + rule1Gid;

          if (!rule1Handle || seenRule1.has(rule1Key)) continue;
          seenRule1.add(rule1Key);

          try {
            var response = await fetch('https://www.g-niib.com/products/' + rule1Handle + '.js');
            if (response.ok) {
              var product = await response.json();
              var numericVariantId = Utils.extractVariantId(rule1Gid);
              var specificVariant = product.variants.find(function(v) { return v.id === numericVariantId; });

              if (specificVariant) {
                products.push({
                  ...product,
                  variants: [specificVariant],
                  _cumcRule: 1,
                  _cumcFixedPrice: rule1Data.fixedPrice,        // null for percentage-discount variants
                  _cumcDiscount: rule1Data.discountRate,         // null for fixed-price variants
                  _cumcMinQuantity: rule1.minQuantity,
                  _cumcMaxQuantity: rule1.maxQuantity,
                  _cumcUsed: rule1Data.used || 0,
                  _cumcRemaining: rule1Data.remaining != null ? rule1Data.remaining : rule1.maxQuantity,
                  _promotionVariantId: numericVariantId
                });
              }
            }
          } catch (error) {
            console.warn('Failed to load Rule 1 product:', error);
          }
        }
      }

      // Load Rule 2 products
      if (rule2?.variants && Object.keys(rule2.variants).length > 0) {
        var seen = new Set();

        for (var variantGid in rule2.variants) {
          var variantData = rule2.variants[variantGid];
          var productHandle = variantData.productHandle;
          var key = productHandle + '-' + variantGid;

          if (!productHandle || seen.has(key)) continue;
          seen.add(key);

          try {
            var response = await fetch('https://www.g-niib.com/products/' + productHandle + '.js');
            if (response.ok) {
              var product = await response.json();
              var numericVariantId = Utils.extractVariantId(variantGid);
              var specificVariant = product.variants.find(function(v) { return v.id === numericVariantId; });

              if (specificVariant) {
                products.push({
                  ...product,
                  variants: [specificVariant],
                  _cumcRule: 2,
                  _cumcDiscount: variantData.discountRate || 0.2,
                  _cumcMaxQuantity: rule2.maxQuantityPerVariant,
                  _cumcUsed: variantData.used || 0,
                  _cumcRemaining: variantData.remaining || rule2.maxQuantityPerVariant,
                  _promotionVariantId: numericVariantId
                });
              }
            }
          } catch (error) {
            console.warn('Failed to load Rule 2 product:', error);
          }
        }
      }

      if (products.length === 0) {
        throw new Error('No products could be loaded');
      }

      return products;
    },

    addToSelection: function(ctx, product, variant, quantity) {
      var cfg = this.getConfig();
      var cumcRule = product._cumcRule;
      var itemKey = Utils.createItemKey(product.id, variant.id);
      var existingItem = CartManager.getItem(ctx.cart, itemKey);

      if (cumcRule === 1) {
        var historicalUsed = product._cumcUsed || 0;
        var alreadySelected = CartManager.getQuantityForVariant(ctx.cart, variant.id);
        var totalAfterAdd = historicalUsed + alreadySelected + quantity;

        if (product._cumcRemaining <= 0) {
          alert('此優惠本月已達購買上限。');
          return false;
        }

        if (totalAfterAdd < cfg.RULE1_MIN) {
          alert('優惠一每款產品最少需購買 ' + cfg.RULE1_MIN + ' 盒（最多 ' + cfg.RULE1_MAX + ' 盒）才可享有折扣。請一次過加入最少 ' + cfg.RULE1_MIN + ' 盒。');
          return false;
        }

        if (totalAfterAdd > cfg.RULE1_MAX) {
          var remaining = cfg.RULE1_MAX - historicalUsed - alreadySelected;
          if (remaining <= 0) {
            alert('優惠一本月已達購買上限 (' + cfg.RULE1_MAX + '件)。');
            return false;
          }
          quantity = remaining;
          alert('已加入 ' + remaining + ' 件，此產品本月已達購買上限。');
        }

        if (existingItem) {
          existingItem.quantity = alreadySelected + quantity;
        } else {
          var item = {
            productId: product.id,
            variantId: variant.id,
            product: product,
            variant: variant,
            quantity: quantity,
            cumcRule: 1,
            basePrice: variant.price,
            promotionType: 'cumc'
          };
          if (product._cumcFixedPrice != null) {
            item.fixedPrice = product._cumcFixedPrice;
            item.discountedPrice = product._cumcFixedPrice * 100;
          } else {
            var rule1DiscountRate = product._cumcDiscount || 0.3;
            item.discount = rule1DiscountRate;
            item.discountedPrice = variant.price * (1 - rule1DiscountRate);
          }
          CartManager.setItem(ctx.cart, itemKey, item);
        }
      } else if (cumcRule === 2) {
        var historicalUsed = CartManager.getVariantUsageFromServer(ctx.cart, variant.id);
        var alreadySelected = CartManager.getQuantityForVariant(ctx.cart, variant.id);
        var totalAfterAdd = historicalUsed + alreadySelected + quantity;
        var perVariantLimit = cfg.RULE2_MAX_PER_VARIANT;

        if (product._cumcRemaining <= 0) {
          alert('此產品本月已達購買上限。');
          return false;
        }

        if (totalAfterAdd > perVariantLimit) {
          var remaining = perVariantLimit - historicalUsed - alreadySelected;
          if (remaining <= 0) {
            alert('此產品本月已達購買上限 (' + perVariantLimit + '件)。');
            return false;
          }
          quantity = remaining;
          alert('已加入 ' + remaining + ' 件，此產品本月已達購買上限。');
        }

        if (existingItem) {
          existingItem.quantity = alreadySelected + quantity;
        } else {
          var discountRate = product._cumcDiscount || 0.2;
          var item = {
            productId: product.id,
            variantId: variant.id,
            product: product,
            variant: variant,
            quantity: quantity,
            cumcRule: 2,
            discount: discountRate,
            discountedPrice: variant.price * (1 - discountRate),
            basePrice: variant.price,
            promotionType: 'cumc'
          };
          CartManager.setItem(ctx.cart, itemKey, item);
        }
      }

      return true;
    },

    renderPriceHtml: function(ctx, product, variant) {
      var cfg = this.getConfig();
      var cumcRule = product._cumcRule;
      var variantId = product._promotionVariantId;

      if (cumcRule === 1) {
        var historicalUsed = product._cumcUsed || 0;
        var selectedInCart = CartManager.getQuantityForVariant(ctx.cart, variantId);
        var totalUsed = historicalUsed + selectedInCart;
        var reachedLimit = totalUsed >= cfg.RULE1_MAX;

        var usageBadge = '<div class="product-usage-badge product-usage-badge-cumc" style="background-color: ' +
          (reachedLimit ? '#d72c0d' : '#ff6b00') + ';">已用 ' + totalUsed + '/' + cfg.RULE1_MAX + '</div>';

        if (product._cumcFixedPrice != null) {
          return usageBadge +
            '<span class="discounted-price">$' + product._cumcFixedPrice.toFixed(2) + ' <small>(10-20件/月)</small></span>' +
            '<span class="original-price">$' + Utils.formatPrice(variant.price) + '</span>';
        } else {
          var rule1DiscountRate = product._cumcDiscount || 0.3;
          var rule1DiscountPercent = Math.round(rule1DiscountRate * 100);
          var rule1DiscountedPrice = variant.price * (1 - rule1DiscountRate);
          return usageBadge +
            '<span class="discounted-price">$' + Utils.formatPrice(rule1DiscountedPrice) + ' <small>(' + rule1DiscountPercent + '%折扣, 10-20件/月)</small></span>' +
            '<span class="original-price">$' + Utils.formatPrice(variant.price) + '</span>';
        }
      } else if (cumcRule === 2) {
        var discountRate = product._cumcDiscount || 0.2;
        var discountPercent = Math.round(discountRate * 100);
        var discountedPrice = variant.price * (1 - discountRate);
        var historicalUsed = CartManager.getVariantUsageFromServer(ctx.cart, variantId);
        var selectedInCart = CartManager.getQuantityForVariant(ctx.cart, variantId);
        var totalUsed = historicalUsed + selectedInCart;
        var reachedLimit = totalUsed >= cfg.RULE2_MAX_PER_VARIANT;

        var usageBadge = '<div class="product-usage-badge product-usage-badge-cumc" style="background-color: ' +
          (reachedLimit ? '#d72c0d' : '#28a745') + ';">已用 ' + totalUsed + '/' + cfg.RULE2_MAX_PER_VARIANT + '</div>';

        return usageBadge +
          '<span class="discounted-price">$' + Utils.formatPrice(discountedPrice) + ' <small>(' + discountPercent + '%折扣, 限' + cfg.RULE2_MAX_PER_VARIANT + '件/月)</small></span>' +
          '<span class="original-price">$' + Utils.formatPrice(variant.price) + '</span>';
      }

      return '<span class="product-price-full">$' + Utils.formatPrice(variant.price) + '</span>';
    },

    renderUsageDisplay: function(ctx, usageInfoEl) {
      var cfg = this.getConfig();
      var rule1 = ctx.cart.promotionData?.promotion?.rule1;
      var rule2 = ctx.cart.promotionData?.promotion?.rule2;

      // Build one usage line per Rule 1 variant (each has its own fixed price / discount + monthly cap)
      var rule1Html = '';
      if (rule1?.variants) {
        for (var rule1Gid in rule1.variants) {
          var v = rule1.variants[rule1Gid];
          var used = v.used || 0;
          var remaining = v.remaining != null ? v.remaining : cfg.RULE1_MAX;
          var reached = remaining <= 0;
          var priceLabel = v.fixedPrice != null
            ? ('折實價$' + v.fixedPrice)
            : (Math.round((v.discountRate || 0.3) * 100) + '%折扣');

          rule1Html += '<div class="usage-item usage-item-cumc">' +
            '<span class="usage-label">優惠一 (' + priceLabel + '，最少買10盒，最多20盒):</span>' +
            '<span class="usage-value" style="color: ' + (reached ? '#d72c0d' : '#28a745') + ';">' +
            '本月已用 ' + used + '/' + cfg.RULE1_MAX + ' (' + (reached ? '已達上限' : '剩餘 ' + remaining + ' 件') + ')' +
            '</span></div>';
        }
      }

      // Update desktop usage info
      usageInfoEl.style.display = 'block';
      usageInfoEl.innerHTML = rule1Html +
        '<div class="usage-item usage-item-cumc">' +
        '<span class="usage-label">優惠二 (其餘產品，均可享優惠價，最多買20盒):</span>' +
        '<span class="usage-value" style="color: #28a745;">' +
        '每款產品本月最多' + (rule2?.maxQuantityPerVariant || cfg.RULE2_MAX_PER_VARIANT) + '件' +
        '</span></div>';

      // Update mobile usage info (same as desktop)
      var mobileUsageInfo = document.getElementById('mobile-usage-info');
      if (mobileUsageInfo) {
        mobileUsageInfo.style.display = 'block';
        mobileUsageInfo.innerHTML = usageInfoEl.innerHTML;
      }
    },

    validateBeforeCheckout: function(ctx) {
      var cfg = this.getConfig();
      var rule1ByVariant = {};

      CartManager.forEach(ctx.cart, function(item) {
        if (item.cumcRule === 1) {
          rule1ByVariant[item.variantId] = (rule1ByVariant[item.variantId] || 0) + item.quantity;
        }
      });

      // Each Rule 1 variant must reach its own monthly minimum
      for (var rule1VariantId in rule1ByVariant) {
        var historicalUsed = CartManager.getVariantUsageFromServer(ctx.cart, rule1VariantId);
        var totalRule1 = historicalUsed + rule1ByVariant[rule1VariantId];

        if (totalRule1 < cfg.RULE1_MIN) {
          var stillNeeded = cfg.RULE1_MIN - totalRule1;
          alert('優惠一每款產品最少需購買 ' + cfg.RULE1_MIN + ' 件。此產品目前已選 ' + totalRule1 + ' 件，還需 ' + stillNeeded + ' 件。');
          return false;
        }
      }
      return true;
    }
  };

  // ============================================
  // LIUYANG Promotion Handler
  // ============================================
  GP.Handlers.Liuyang = {
    getConfig: function() {
      return Config.LIUYANG;
    },

    validatePeriod: function(ctx) {
      var periodInfo = ctx.cart.promotionData?.periodInfo;
      if (!periodInfo?.isActive) {
        throw new Error('此優惠期限為2026年1月9日至15日。如有疑問，請聯絡客戶服務。');
      }
    },

    loadProducts: GP.Handlers.IbdGroup.loadProducts,

    addToSelection: function(ctx, product, variant, quantity) {
      var cfg = this.getConfig();
      var perVariantLimit = ctx.cart.promotionData?.promotion?.maxQuantityPerVariant || cfg.MAX_PER_VARIANT;
      var alreadyUsed = CartManager.getVariantUsageFromServer(ctx.cart, variant.id);
      var alreadySelected = CartManager.getQuantityForVariant(ctx.cart, variant.id);
      var totalAfterAdd = alreadyUsed + alreadySelected + quantity;

      if (totalAfterAdd > perVariantLimit) {
        var remaining = perVariantLimit - alreadyUsed - alreadySelected;
        if (remaining <= 0) {
          alert('此產品已達購買上限 (' + perVariantLimit + '件)');
          return false;
        }
        quantity = remaining;
        alert('已加入 ' + remaining + ' 件，此產品已達購買上限');
      }

      var itemKey = Utils.createItemKey(product.id, variant.id);
      var existingItem = CartManager.getItem(ctx.cart, itemKey);
      var discountedPrice = variant.price * 0.8; // 20% off

      if (existingItem) {
        existingItem.quantity += quantity;
      } else {
        var item = {
          productId: product.id,
          variantId: variant.id,
          product: product,
          variant: variant,
          quantity: quantity,
          discountedPrice: discountedPrice,
          basePrice: variant.price,
          promotionType: 'liuyang'
        };
        CartManager.setItem(ctx.cart, itemKey, item);
      }
      return true;
    },

    recalculatePricing: function(ctx) {
      var cfg = this.getConfig();
      var totalQuantity = CartManager.getTotalQuantity(ctx.cart);
      var hasDiscount = totalQuantity >= cfg.RULE1_MIN_QUANTITY;

      CartManager.forEach(ctx.cart, function(item) {
        item.discountedPrice = hasDiscount ? item.basePrice * 0.8 : item.basePrice;
        item.hasDiscount = hasDiscount;
      });
    },

    calculateGifts: function(totalQuantity) {
      var cfg = this.getConfig();
      var sets = Math.floor(totalQuantity / cfg.GIFT_MULTIPLE);
      return sets * cfg.GIFT_QUANTITY;
    },

    renderPriceHtml: function(ctx, product, variant) {
      var cfg = this.getConfig();
      var perVariantLimit = ctx.cart.promotionData?.promotion?.maxQuantityPerVariant || cfg.MAX_PER_VARIANT;
      var alreadyUsed = CartManager.getVariantUsageFromServer(ctx.cart, variant.id);
      var selectedInCart = CartManager.getQuantityForVariant(ctx.cart, variant.id);
      var totalUsed = alreadyUsed + selectedInCart;

      var usageBadge = '<div class="product-usage-badge">已用 ' + totalUsed + '/' + perVariantLimit + '</div>';

      var discountedPrice = variant.price * 0.8;
      return usageBadge +
        '<span class="discounted-price">$' + Utils.formatPrice(discountedPrice) + ' <small>(買2件或以上享8折)</small></span>' +
        '<span class="original-price">$' + Utils.formatPrice(variant.price) + '</span>';
    },

    renderUsageDisplay: function(ctx, usageInfoEl) {
      var cfg = this.getConfig();
      var totalQuantity = CartManager.getTotalQuantity(ctx.cart);
      var giftsEarned = this.calculateGifts(totalQuantity);
      var needsMore = cfg.RULE1_MIN_QUANTITY - totalQuantity;

      var html = '<div class="usage-item">' +
        '<span class="usage-label">優惠一 (買2盒或以上，可享8折):</span>' +
        '<span class="usage-value" style="color: #28a745;">' +
        (totalQuantity >= 2 ? '✓ 已達標 (8折)' : '還需' + needsMore + '件') +
        '</span></div>' +
        '<div class="usage-item">' +
        '<span class="usage-label">優惠二 (每買滿8盒，再送纖肝代謝配方(7天裝)四盒，多買多送):</span>' +
        '<span class="usage-value" style="color: #28a745;">有效</span></div>';

      if (totalQuantity > 0) {
        html += '<div class="usage-item">' +
          '<span class="usage-label">🎁 贈品:</span>' +
          '<span class="usage-value">' + giftsEarned + '盒 (每滿8盒送4盒)</span></div>';
      }

      // Update desktop usage info
      usageInfoEl.style.display = 'block';
      usageInfoEl.innerHTML = html;

      // Update mobile usage info
      var mobileUsageInfo = document.getElementById('mobile-usage-info');
      if (mobileUsageInfo) {
        mobileUsageInfo.style.display = 'block';
        mobileUsageInfo.innerHTML = html;
      }
    }
  };

  // ============================================
  // DCH Auriga Promotion Handler
  // ============================================
  GP.Handlers.DchAuriga = {
    getConfig: function() {
      return Config.DCH_AURIGA;
    },

    validatePeriod: function(ctx) {
      var periodInfo = ctx.cart.promotionData?.periodInfo;
      if (!periodInfo?.isActive) {
        throw new Error('此優惠期限為2026年1月27日至29日。如有疑問，請聯絡客戶服務。');
      }
    },

    loadProducts: async function(ctx) {
      var includedVariants = ctx.cart.promotionData?.promotion?.includedVariants;
      if (!includedVariants || includedVariants.length === 0) {
        throw new Error('No included variants found in promotion data');
      }

      var products = [];
      var seen = new Set();

      for (var i = 0; i < includedVariants.length; i++) {
        var variantData = includedVariants[i];
        var key = variantData.productHandle + '-' + variantData.variantId;
        if (seen.has(key)) continue;
        seen.add(key);

        try {
          var response = await fetch('https://www.g-niib.com/products/' + variantData.productHandle + '.js');
          if (response.ok) {
            var product = await response.json();
            var numericVariantId = Utils.extractVariantId(variantData.variantId);
            var specificVariant = product.variants.find(function(v) { return v.id === numericVariantId; });

            if (specificVariant) {
              products.push({
                ...product,
                variants: [specificVariant],
                _dchAurigaDiscountPercentage: variantData.discountPercentage,
                _dchAurigaDiscountRate: variantData.discountPercentage / 100,
                _promotionVariantId: numericVariantId
              });
            }
          }
        } catch (error) {
          console.warn('Failed to load product:', error);
        }
      }

      if (products.length === 0) {
        throw new Error('No products could be loaded');
      }

      return products;
    },

    addToSelection: function(ctx, product, variant, quantity) {
      var cfg = this.getConfig();
      var currentTotal = CartManager.getTotalQuantity(ctx.cart);
      var newTotal = currentTotal + quantity;

      if (newTotal > cfg.MAX_QUANTITY) {
        alert('DCH Auriga 優惠最多只能購買 ' + cfg.MAX_QUANTITY + ' 件產品。目前已有 ' + currentTotal + ' 件。');
        return false;
      }

      var itemKey = Utils.createItemKey(product.id, variant.id);
      var existingItem = CartManager.getItem(ctx.cart, itemKey);
      var productDiscount = this.getProductDiscount(product);

      if (existingItem) {
        existingItem.quantity += quantity;
      } else {
        var item = {
          productId: product.id,
          variantId: variant.id,
          product: product,
          variant: variant,
          quantity: quantity,
          discountPercentage: productDiscount,
          discountedPrice: variant.price * (1 - productDiscount / 100),
          basePrice: variant.price,
          quantityWithDiscount: 0,
          quantityFullPrice: 0,
          promotionType: 'dch_auriga'
        };
        CartManager.setItem(ctx.cart, itemKey, item);
      }
      return true;
    },

    recalculatePricing: function(ctx) {
      // ALL-OR-NOTHING logic:
      // - If total quantity is EVEN AND >= MIN_QUANTITY: ALL items get discount
      // - If total quantity is ODD or < MIN_QUANTITY: NO items get discount
      var cfg = this.getConfig();
      var totalQuantity = CartManager.getTotalQuantity(ctx.cart);
      var isUsed = ctx.cart.promotionData?.usage?.used;
      var isEven = totalQuantity % 2 === 0;
      var allGetDiscount = isEven && totalQuantity >= cfg.MIN_QUANTITY && !isUsed;

      CartManager.forEach(ctx.cart, function(item) {
        if (allGetDiscount) {
          item.quantityWithDiscount = item.quantity;
          item.quantityFullPrice = 0;
        } else {
          item.quantityWithDiscount = 0;
          item.quantityFullPrice = item.quantity;
        }
      });
    },

    getProductDiscount: function(product) {
      return product._dchAurigaDiscountPercentage || 20;
    },

    calculateItemTotal: function(ctx, item) {
      var discountedTotal = item.discountedPrice * item.quantityWithDiscount;
      var fullPriceTotal = item.basePrice * item.quantityFullPrice;
      return discountedTotal + fullPriceTotal;
    },

    renderPriceHtml: function(ctx, product, variant) {
      var isUsed = ctx.cart.promotionData?.usage?.used;
      var productDiscount = this.getProductDiscount(product);

      if (isUsed) {
        return '<span class="product-price-full">$' + Utils.formatPrice(variant.price) + '</span>';
      }

      var discountedPrice = variant.price * (1 - productDiscount / 100);
      var discountLabel = productDiscount === 30 ? '7折' : '8折';
      return '<span class="discounted-price">$' + Utils.formatPrice(discountedPrice) + ' <small>(購買 雙數數量 即享 ' + discountLabel + ' 優惠)</small></span>' +
             '<span class="original-price">$' + Utils.formatPrice(variant.price) + '</span>';
    },

    renderUsageDisplay: function(ctx, usageInfoEl) {
      var cfg = this.getConfig();
      var totalQuantity = CartManager.getTotalQuantity(ctx.cart);
      var isUsed = ctx.cart.promotionData?.usage?.used;
      var needsMore = cfg.MIN_QUANTITY - totalQuantity;
      var isOddQuantity = totalQuantity % 2 === 1;
      var mobileUsageInfo = document.getElementById('mobile-usage-info');

      usageInfoEl.style.display = 'block';

      if (isUsed) {
        var usedHtml = '<div class="usage-item usage-item-dch-auriga">' +
          '<span class="usage-label" style="color: #d72c0d;">已使用:</span>' +
          '<span class="usage-value" style="color: #d72c0d;">此優惠已使用</span>' +
          '</div>';
        usageInfoEl.innerHTML = usedHtml;
        if (mobileUsageInfo) {
          mobileUsageInfo.style.display = 'block';
          mobileUsageInfo.innerHTML = usedHtml;
        }
        return;
      }

      var html = '<div class="usage-item usage-item-dch-auriga">' +
        '<span class="usage-label">已選數量:</span>' +
        '<span class="usage-value">' + totalQuantity + ' / ' + cfg.MAX_QUANTITY + '</span>' +
        '</div>';

      if (needsMore > 0) {
        html += '<div class="usage-item usage-item-dch-auriga">' +
          '<span class="usage-label" style="color: #ff6b00;">還需:</span>' +
          '<span class="usage-value" style="color: #ff6b00;">' + needsMore + ' 件 (享優惠)</span>' +
          '</div>';
      }

      if (isOddQuantity && totalQuantity >= cfg.MIN_QUANTITY) {
        html += '<div class="usage-item usage-item-dch-auriga">' +
          '<span class="usage-label" style="color: #00a86b;">提示:</span>' +
          '<span class="usage-value" style="color: #00a86b;">再加1件即享優惠價！</span>' +
          '</div>';
      } else if (!isOddQuantity && totalQuantity >= cfg.MIN_QUANTITY) {
        html += '<div class="usage-item usage-item-dch-auriga">' +
          '<span class="usage-label" style="color: #28a745;">✓ 已符合:</span>' +
          '<span class="usage-value" style="color: #28a745;">雙數優惠</span>' +
          '</div>';
      }

      // Update desktop usage info
      usageInfoEl.innerHTML = html;

      // Update mobile usage info
      if (mobileUsageInfo) {
        mobileUsageInfo.style.display = 'block';
        mobileUsageInfo.innerHTML = html;
      }
    }
  };

})(window);
